<template>
  <div>
    <div v-if="needsPayerNotice" class="payer-notice mb-2">
      <v-icon size="small" class="mr-1">mdi-information-outline</v-icon>
      <span v-if="payerEmail">
        {{ blockedNames }} {{ needSeats.length === 1 ? 'needs a seat' : 'need seats' }} on your plan.
        Contact <strong>{{ payerEmail }}</strong> to add {{ needSeats.length === 1 ? 'it' : 'them' }}.
      </span>
      <span v-else>
        {{ blockedNames }} {{ needSeats.length === 1 ? 'needs a seat' : 'need seats' }} on your plan,
        which only the person who pays for it can add.
      </span>
    </div>
    <div v-if="showSeatUsage" class="payer-notice mb-2">
      <v-icon size="small" class="mr-1">mdi-information-outline</v-icon>
      <span>
        This adds <strong>{{ needSeats.length }} {{ needSeats.length === 1 ? 'seat' : 'seats' }}</strong> to your plan —
        <strong>{{ seatsAfterInvite }} of {{ seatLimit }}</strong> used after this invite.
        <template v-if="seatsAfterInvite > seatLimit">
          This is over your agreement, so your Qlerify Account Manager will contact you to adjust your plan.
        </template>
      </span>
    </div>
    <div class="add-team-member-container mb-4" @mousedown="focus">
      <div class="d-flex flex-wrap">
        <div
          v-for="email in addMembers"
          :key="email"
          class="add-team-member-preview flex-shrink-0"
          @mousedown.stop
        >
          {{ email }}
          <v-btn
            variant="plain"
            size="small"
            icon="mds:close"
            @click="removeMember(email)"
            :disabled="isWorkflowDisabled"
          />
        </div>
      </div>
      <v-text-field
        v-model="email"
        ref="textField"
        variant="solo"
        class="add-team-member-field"
        placeholder="Add people by email, comma separated"
        single-line
        hide-details
        clear-icon="mds:close"
        clearable
        @mousedown.stop
        @blur="refreshAddTeamMember"
        @keydown.enter="refreshAddTeamMember"
        :disabled="isWorkflowDisabled"
        autocomplete="off"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { validateEmail } from '@/utils/validate'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  // Owned by the parent's seat confirmation so the notice and the charge agree on who needs a seat.
  needSeats: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue'])

const store = useStore()

const email = ref('')
const addMembers = ref([])
const textField = ref(null)

const isWorkflowDisabled = computed(() => store.state.isWorkflowDisabled)
const isSubscriptionAdmin = computed(() => store.state.payments.isSubscriptionAdmin)
const payerEmail = computed(() => store.state.payments.activeSubscription?.payerUserEmail || '')
const needsPayerNotice = computed(() => !isSubscriptionAdmin.value && props.needSeats.length > 0)
const blockedNames = computed(() => props.needSeats.length === 1
  ? props.needSeats[0]
  : `${props.needSeats.length} of these people`)

// Enterprise seats are agreed up front rather than bought per invite, so going over is allowed
// and settled with the Qlerify Account Manager instead of blocking the invite.
const seatLimit = computed(() => store.state.payments.activeSubscription?.subscriptionDetails?.quantity || 0)
const seatedEmails = computed(() => (store.state.payments.subscriptionUsers || []).map((user) => user.email))
const seatsAfterInvite = computed(() => seatedEmails.value.length + props.needSeats.length)
const showSeatUsage = computed(() => isSubscriptionAdmin.value &&
  store.state.payments.isEnterpriseUser &&
  seatLimit.value > 0 &&
  props.needSeats.length > 0)

const focus = ($event) => {
  textField.value?.focus()
  if ($event) {
    $event.stopPropagation()
    $event.preventDefault()
  }
}

const removeMember = (emailToRemove) => {
  addMembers.value = addMembers.value.filter(it => it !== emailToRemove)
}

const refreshAddTeamMember = () => {
  if (!email.value) {
    email.value = ''
    return
  }

  const inputEmails = email.value.split(',').map(it => it.trim().toLowerCase()).filter(Boolean)
  const newInvalidEmails = []
  const newValidEmailsToAdd = []

  inputEmails.forEach(inputEmail => {
    if (validateEmail(inputEmail)) {
      if (!addMembers.value.includes(inputEmail)) {
        newValidEmailsToAdd.push(inputEmail)
      }
    } else {
      newInvalidEmails.push(inputEmail)
    }
  })

  addMembers.value = [...new Set([...addMembers.value, ...newValidEmailsToAdd])]
  email.value = newInvalidEmails.join(',')
}

watch(() => props.modelValue, (newVal) => {
  if (newVal.length !== addMembers.value.length || newVal.some(e => !addMembers.value.includes(e))) {
    addMembers.value = [...newVal]
  }
  if (newVal.length === 0) {
    email.value = ''
  }
}, { deep: true, immediate: true })

watch(addMembers, (newVal) => {
  emit('update:modelValue', newVal)
}, { deep: true })
</script>

<style>
.add-team-member-field {
  min-height: 14px !important;
  max-height: 14px !important;
  height: 14px !important;
  margin-right: -12px;
}

.add-team-member-field .v-field.v-field--variant-solo {
  background-color: transparent;
  box-shadow: none !important;
}

.add-team-member-field .v-field__input {
  padding: 0 !important;
  min-height: 16px !important;
  height: 16px !important;
  font-size: 14px !important;
  line-height: 16px !important;
}
</style>

<style scoped>
.add-team-member-container {
  cursor: text;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 52px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #CFD3D9;
}

[data-theme="dark"] .add-team-member-container :deep(.v-field) {
  background-color: transparent !important;
}

.payer-notice {
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  line-height: 18px;
  color: var(--text-light, #6B7280);
}

.add-team-member-preview {
  padding: 6px;
  border-radius: 4px;
  margin-bottom: 8px;
  margin-right: 8px;
  border: 1px solid #CFD3D9;
}

.access-level-select-container span {
  margin: 0 5px;
}

.access-level-select :deep(fieldset) {
  border-style: none;
}

.hidden {
  display: none;
}
</style>
